/** Weather condition info helpers */

import type { TranslationKey } from '@/i18n/types';

interface ConditionInfo {
  icon: string;
  label: string;
}

interface ConditionEntry {
  icon: string;
  key: TranslationKey;
}

const CONDITION_MAP: Record<string, ConditionEntry> = {
  CLEAR:         { icon: '☀️', key: 'weather.clear' },
  PARTLY_CLOUDY: { icon: '⛅', key: 'weather.partlyCloudy' },
  OVERCAST:      { icon: '☁️', key: 'weather.overcast' },
  FOG:           { icon: '🌫️', key: 'weather.fog' },
  DRIZZLE:       { icon: '🌦️', key: 'weather.drizzle' },
  RAIN:          { icon: '🌧️', key: 'weather.rain' },
  FREEZING_RAIN: { icon: '🧊', key: 'weather.freezingRain' },
  SNOW:          { icon: '❄️', key: 'weather.snow' },
  RAIN_SHOWERS:  { icon: '🌧️', key: 'weather.rainShowers' },
  SNOW_SHOWERS:  { icon: '🌨️', key: 'weather.snowShowers' },
  THUNDERSTORM:  { icon: '⛈️', key: 'weather.thunderstorm' },
};

const BY_INDEX = [null, 'CLEAR', 'PARTLY_CLOUDY', 'OVERCAST', 'FOG', 'DRIZZLE',
  'RAIN', 'FREEZING_RAIN', 'SNOW', 'RAIN_SHOWERS', 'SNOW_SHOWERS', 'THUNDERSTORM'];

export function weatherConditionInfo(cond: string | number, t: (key: TranslationKey) => string): ConditionInfo {
  if (typeof cond === 'string') {
    if (CONDITION_MAP[cond]) {
      const entry = CONDITION_MAP[cond];
      return { icon: entry.icon, label: t(entry.key) };
    }
    const c = cond.toLowerCase();
    if (c.includes('thunder')) return { icon: '⛈️', label: cond };
    if (c.includes('snow')) return { icon: '❄️', label: cond };
    if (c === 'freezing rain') return { icon: '🧊', label: cond };
    if (c.includes('rain') || c.includes('shower')) return { icon: '🌧️', label: cond };
    if (c.includes('freezing') && c.includes('drizzle')) return { icon: '🧊', label: cond };
    if (c.includes('drizzle')) return { icon: '🌦️', label: cond };
    if (c.includes('fog') || c.includes('mist')) return { icon: '🌫️', label: cond };
    if (c === 'partly cloudy') return { icon: '⛅', label: cond };
    if (c.includes('cloud') || c.includes('overcast')) return { icon: '☁️', label: cond };
    if (c.includes('clear') || c.includes('sun')) return { icon: '☀️', label: cond };
  }
  if (typeof cond === 'number' && BY_INDEX[cond]) {
    const key = BY_INDEX[cond]!;
    const entry = CONDITION_MAP[key];
    if (entry) return { icon: entry.icon, label: t(entry.key) };
  }
  return { icon: '❓', label: t('weather.unknown') };
}

const WET_CONDS = ['DRIZZLE', 'RAIN', 'FREEZING_RAIN', 'SNOW', 'RAIN_SHOWERS', 'SNOW_SHOWERS', 'THUNDERSTORM'];

export function isSessionWet(weather?: { condition: string | number }): boolean {
  if (!weather) return false;
  const cond = weather.condition;
  const condStr = typeof cond === 'string' ? cond : String(cond);
  return WET_CONDS.includes(condStr);
}
